import { useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import type { ChartOptions, TooltipItem } from "chart.js";
import { Line } from "react-chartjs-2";
import type { EnrollmentCohortCurvesResponse } from "@t3tools/contracts";
import { Button } from "~/components/ui/button";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
} from "~/components/ui/combobox";
import { ChartPanel, DashboardFilterField, DashboardFilterRow } from "./dashboardKit";
import { cohortDataset, cohortLabel, cohortStyle } from "./enrollmentCohortCurves";

ChartJS.register(LinearScale, LineElement, PointElement, Tooltip, Legend);

export function EnrollmentCohortCurvesTab(props: {
  data: EnrollmentCohortCurvesResponse | undefined;
  error: string;
  loading: boolean;
  onRetry: () => void;
}) {
  const [clinicId, setClinicId] = useState("");
  const [hiddenByClinic, setHiddenByClinic] = useState<Record<string, string[]>>({});
  if (props.loading)
    return (
      <p role="status" className="py-12 text-sm text-muted-foreground">
        Loading cohort curves…
      </p>
    );
  if (props.error)
    return (
      <div role="alert" className="space-y-3 rounded-lg border border-border p-4">
        <p className="text-sm">{props.error}</p>
        <Button variant="outline" onClick={props.onRetry}>
          Try again
        </Button>
      </div>
    );
  const data = props.data;
  if (!data) return null;
  const clinic =
    data.clinics.find((item) => item.id === clinicId) ??
    data.clinics.find((item) => item.name === "Memory Treatment Centers") ??
    data.clinics[0];
  if (!clinic) return <p className="text-sm text-muted-foreground">No clinics available.</p>;
  const months = [
    ...new Set(data.clinics.flatMap((item) => item.cohorts.map((cohort) => cohort.month))),
  ].sort();
  const hidden = hiddenByClinic[clinic.id] ?? [];
  const visible = clinic.cohorts.filter((cohort) => !hidden.includes(cohort.month));
  const setHidden = (next: string[]) =>
    setHiddenByClinic((current) => ({ ...current, [clinic.id]: next }));
  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "nearest", intersect: false },
    scales: {
      x: {
        type: "linear",
        min: 0,
        title: { display: true, text: "Days since referral" },
        ticks: { precision: 0 },
      },
      y: {
        min: 0,
        max: 100,
        title: { display: true, text: "Cumulative enrollment (%)" },
        ticks: { callback: (value) => `${value}%` },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<"line">[]) => `Day ${items[0]?.parsed.x ?? 0}`,
          label: (item) => {
            const cohort = visible[item.datasetIndex];
            const point = cohort?.points[item.dataIndex];
            return point && cohort
              ? `${cohortLabel(cohort)}: ${point.enrolled}/${cohort.size} enrolled (${point.percent.toFixed(1)}%)`
              : "";
          },
          afterLabel: (item) =>
            (item.parsed.x ?? 0) > (visible[item.datasetIndex]?.fullyObservedDays ?? Infinity)
              ? "Some patients have not reached this follow-up day."
              : "",
        },
      },
    },
  };

  return (
    <div className="flex flex-col gap-4">
      <DashboardFilterRow
        trailing={`Updated ${new Date(data.asOf).toLocaleString("en-US", { timeZone: "UTC" })} UTC`}
      >
        <DashboardFilterField label="Clinic">
          <Combobox
            items={data.clinics}
            value={clinic}
            itemToStringLabel={(item) => item.name}
            itemToStringValue={(item) => item.id}
            onValueChange={(item) => {
              if (item) setClinicId(item.id);
            }}
          >
            <ComboboxInput
              aria-label="Clinic"
              placeholder="Search clinics…"
              className="w-full sm:w-96"
            />
            <ComboboxPopup>
              <ComboboxEmpty>No clinics found.</ComboboxEmpty>
              <ComboboxList>
                {(item) => (
                  <ComboboxItem key={item.id} value={item}>
                    {item.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxPopup>
          </Combobox>
        </DashboardFilterField>
      </DashboardFilterRow>
      <ChartPanel
        title={`${clinic.name} enrollment cohorts`}
        action={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setHidden([])}
              disabled={!clinic.cohorts.length}
            >
              Show all
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setHidden(clinic.cohorts.map((cohort) => cohort.month))}
              disabled={!clinic.cohorts.length}
            >
              Hide all
            </Button>
          </div>
        }
      >
        {clinic.cohorts.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            No eligible cohorts for this clinic.
          </p>
        ) : (
          <>
            <fieldset className="mb-5">
              <legend className="mb-2 text-xs font-medium text-muted-foreground">
                Enable cohorts to compare
              </legend>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {clinic.cohorts.map((cohort) => {
                  const style = cohortStyle(months.indexOf(cohort.month));
                  return (
                    <label
                      key={cohort.month}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={!hidden.includes(cohort.month)}
                        onChange={() =>
                          setHidden(
                            hidden.includes(cohort.month)
                              ? hidden.filter((month) => month !== cohort.month)
                              : [...hidden, cohort.month],
                          )
                        }
                      />
                      <svg width="32" height="10" aria-hidden="true" className="shrink-0">
                        <line
                          x1="0"
                          x2="32"
                          y1="5"
                          y2="5"
                          stroke={style.color}
                          strokeWidth="2.5"
                          strokeDasharray={style.dash.join(" ")}
                        />
                      </svg>
                      <span>{cohortLabel(cohort)}</span>
                      <span className="ml-auto whitespace-nowrap text-muted-foreground">
                        {cohort.converted}/{cohort.size} ·{" "}
                        {((100 * cohort.converted) / cohort.size).toFixed(1)}%
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            {visible.length ? (
              <div className="h-[440px] w-full min-w-0">
                <Line
                  aria-label={`${clinic.name} enrollment cohort curves`}
                  role="img"
                  data={{
                    datasets: visible.map((cohort) =>
                      cohortDataset(cohort, months.indexOf(cohort.month)),
                    ),
                  }}
                  options={options}
                />
              </div>
            ) : (
              <p role="status" className="py-20 text-center text-sm text-muted-foreground">
                Select a cohort to display its curve.
              </p>
            )}
          </>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Each line ends at its available follow-up. Faded segments show days not yet reached by
          every patient in that cohort.
        </p>
      </ChartPanel>
      {clinic.undatedCount > 0 ? (
        <p className="text-sm text-muted-foreground">
          {clinic.undatedCount.toLocaleString("en-US")} eligible{" "}
          {clinic.undatedCount === 1 ? "Carespace has" : "Carespaces have"} no reliable referral
          date for this clinic and are omitted from dated cohorts. Profiles from other clinics and
          unmatched Carespaces created before this clinic existed are not used as referral dates.
        </p>
      ) : null}
      <details className="rounded-lg border border-border/70 p-4 text-sm">
        <summary className="cursor-pointer font-medium">How cohorts are calculated</summary>
        <div className="mt-3 space-y-2 text-muted-foreground">
          <p>
            Cohorts group referrals by month in UTC. Use the earliest matching ESPO profile creation
            date at the same clinic, including Carespaces imported on or after June 16, 2026.
            Otherwise use Carespace creation when there is no conflicting clinic match.
          </p>
          <p>
            Archived Carespaces and those currently marked ineligible for GUIDE are excluded. New
            Carespaces remain in the cohort denominator.
          </p>
          <p>
            Enrollment means ever reaching GUIDE Submitted or Awaiting additional referral
            information. Use the earliest qualifying event and the earlier of its recorded and
            effective timestamps. Negative intervals count at day 0.
          </p>
          <p>
            Matches use stored confirmed links or exact MBI, MRN plus date of birth, or first and
            last name plus date of birth within the clinic.
          </p>
        </div>
      </details>
    </div>
  );
}
